import { useEffect, useState } from "react";
import { onlineManager, useQuery } from "@tanstack/react-query";

import { env } from "~/env";

interface NetworkResponse extends Response {
  ok: boolean;
  status: number;
}

type XHRResponseType =
  | ""
  | "arraybuffer"
  | "blob"
  | "document"
  | "json"
  | "text";

interface TypedResponse<T> extends XMLHttpRequest {
  response: T;
  responseType: XHRResponseType;
}

const convertXHRResponseToBodyInit = (xhr: XMLHttpRequest): BodyInit => {
  switch (xhr.responseType) {
    case "blob":
      return xhr.response as Blob;
    case "arraybuffer":
      return xhr.response as ArrayBuffer;
    case "text":
    case "":
      return xhr.response as string;
    case "json":
      return JSON.stringify(xhr.response);
    default:
      return String(xhr.response); // fallback
  }
};

const fetchWithTimeoutFallback = async (
  url: string,
  timeout: number,
): Promise<NetworkResponse> => {
  // Modern browsers with AbortController support
  if ("AbortController" in window) {
    const controller = new AbortController();
    const signal = controller.signal;
    const fetchPromise = fetch(url, { signal });
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetchPromise;
      clearTimeout(timeoutId);
      return response as NetworkResponse;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  // Fallback for older browsers
  return new Promise<NetworkResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest() as TypedResponse<string>;
    xhr.responseType = "text";
    xhr.timeout = timeout;
    xhr.open("GET", url);

    xhr.onload = () => {
      const responseBody = convertXHRResponseToBodyInit(xhr);
      const response = new Response(responseBody, {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: new Headers({
          "Content-Type": xhr.getResponseHeader("Content-Type") ?? "text/plain",
        }),
      }) as NetworkResponse;
      resolve(response);
    };

    xhr.onerror = () => reject(new Error("Network request failed"));
    xhr.ontimeout = () => reject(new Error("Network request timed out"));

    xhr.send();
  });
};

interface NetworkInformation extends EventTarget {
  addEventListener: (
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ) => void;
  removeEventListener: (
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ) => void;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
}

function useNetworkStatus(): boolean {
  const [isOnline, setIsOnline] = useState<boolean>(true);

  const {
    isError,
    isLoading,
    refetch: healthCheck,
  } = useQuery({
    queryKey: ["healthCheck"],
    queryFn: async (): Promise<NetworkResponse> => {
      try {
        return await fetchWithTimeoutFallback(
          `${env.VITE_API_URL}/health-check`,
          5000,
        );
      } catch (error) {
        console.error("Health check failed:", error);
        throw error;
      }
    },
    networkMode: "always",
    retry: 1,
  });

  useEffect(() => {
    const handleOnline = (): void => {
      setIsOnline(true);
      void healthCheck();
    };

    const handleOffline = (): void => {
      setIsOnline(false);
      onlineManager.setOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [healthCheck]);

  useEffect(() => {
    const checkServerStatus = (): void => {
      if (document.visibilityState === "visible") {
        void healthCheck();
      }
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        checkServerStatus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    checkServerStatus();
    const interval = setInterval(checkServerStatus, 15000);

    const nav = navigator as NavigatorWithConnection;
    if (nav.connection) {
      nav.connection.addEventListener("change", checkServerStatus);
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(interval);

      if (nav.connection) {
        nav.connection.removeEventListener("change", checkServerStatus);
      }
    };
  }, [healthCheck]);

  useEffect(() => {
    if (isError) {
      setIsOnline(false);
      onlineManager.setOnline(false);
    } else if (!isLoading) {
      setIsOnline(true);
      onlineManager.setOnline(true);
    }
  }, [isError, isLoading]);

  return isOnline;
}

export default useNetworkStatus;
